import type { ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { CageProvider } from '@/contexts/cage';
import { CageFilterProvider, readSelectedFishGroupId } from '@/contexts/cage-filter-context';
import { PenCohortProvider } from '@/contexts/pen-cohort-context';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';

import { CageOverviewProvider } from './components/cage-overview-context';

/**
 * Wraps cage page content with the required providers
 * (CageProvider + CageFilterProvider + CageOverviewProvider).
 *
 * This is the single mount point for these providers across every cage route —
 * including the wounds sub-tree, which previously had its own duplicate wrapper.
 *
 * This wrapper is scoped to the content area only — the sidebar (AppSidebar)
 * lives outside this tree and fetches its own cage data independently.
 * This prevents full re-renders of the sidebar when cage context loads.
 *
 * `CageOverviewProvider` is mounted here so cage-level pages can read the
 * overview without each container wrapping it manually. The legacy per-page
 * manual provider wraps are gone.
 *
 * `PenCohortProvider` is mounted for the same reason, and because it is the
 * outermost place that has both the Router and the cage context: `PageHeader`
 * then names the cohort on every cage screen without thirty containers each
 * asking for it.
 */

interface CagePageWrapperProps {
    children: ReactNode;
}

const DECIMAL_CAGE_ID_PATTERN = /^\d+$/;

function getSafeCageId(cageId: string | undefined): number {
    if (cageId == null || !DECIMAL_CAGE_ID_PATTERN.test(cageId)) {
        return 0;
    }

    const parsedCageId = Number(cageId);

    return Number.isSafeInteger(parsedCageId) && parsedCageId > 0 ? parsedCageId : 0;
}

export function CagePageWrapper(props: CagePageWrapperProps) {
    const { children } = props;
    const { cageId } = useParams<{ cageId?: string }>();
    const { search } = useLocation();
    const { enabled: fishGroupsEnabled } = usePostHogFeatureFlag('fish-group-tracking');

    // `CageProvider` sits above `CageFilterProvider`, so it cannot read `selectedFishGroupId`.
    // The same URL parse and the same flag gate give it the same answer the filter gives
    // every request below it.
    const fishGroupId = fishGroupsEnabled ? readSelectedFishGroupId(search) : undefined;

    return (
        <CageProvider cageId={getSafeCageId(cageId)} fishGroupId={fishGroupId}>
            <CageFilterProvider>
                <CageOverviewProvider>
                    <PenCohortProvider>{children}</PenCohortProvider>
                </CageOverviewProvider>
            </CageFilterProvider>
        </CageProvider>
    );
}
